import {
  ContentIcon,
  MathShowcase,
  SearchShowcase,
} from "@dropbox/dash-component-library";
import { Avatar } from "@dropbox/dig-components/avatar";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box, ThemeContainer, ThemeProvider } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { LinkLine } from "@dropbox/dig-icons/assets";
import { DashStackIconWithStack } from "@mirage/mosaics/DCLWrappers/DashStackIcon";
import { getTimeAgoStringFromTimestamp } from "@mirage/search/SearchResults/util/resultUtil";
import { tagged } from "@mirage/service-logging";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import { getInitials } from "@mirage/shared/account/user_i18n";
import {
  DesktopFileIcon,
  getExtension,
  URLShortcutIcon,
  UrlSrcIcon,
} from "@mirage/shared/icons";
import DesktopApplicationIcon from "@mirage/shared/icons/DesktopApplicationIcon";
import { isRecordTypeTagEvent } from "@mirage/shared/search/search-result";
import { faviconSrcForSrcUrl } from "@mirage/shared/util/favicon";
import { getTheme } from "@mirage/stacks/themes/utils";
import i18n from "@mirage/translations";
import {
  BASE_PREVIEW_ICON_SIZE,
  getCalendarEventInfo,
  getChatPreview,
  getContentInfo,
  PreviewIconContainer,
} from "./previews";

import type { ContentInfoPanelProps } from "@dropbox/dash-component-library";
import type { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";

const logger = tagged("get-typeahead-result-info");

export const getTypeaheadResultInfo = (
  result: TypeaheadResult,
  isDarkMode: boolean,
): ContentInfoPanelProps => {
  const { type, scoredResult } = result;

  let info: Partial<ContentInfoPanelProps> = {};
  switch (scoredResult.type) {
    case typeahead.ResultType.Stack: {
      const stack = scoredResult.result;
      info = {
        contentPreview: (
          <ThemeProvider
            overrides={
              getTheme(
                stack?.stack_data?.color_index,
                stack?.stack_data?.custom_color,
              ).subtle
            }
          >
            <ThemeContainer>
              <PreviewIconContainer
                icon={<DashStackIconWithStack size="xlarge" stack={stack} />}
              />
            </ThemeContainer>
          </ThemeProvider>
        ),
        // Temporary. Unique stack treatment coming down the road.
        attributes: [
          {
            label: i18n.t("last_updated_label"),
            value: getTimeAgoStringFromTimestamp(
              stack.last_modified_time_utc_sec,
            ),
          },
        ],
      };
      break;
    }
    case typeahead.ResultType.StackItem: {
      const stackItem = scoredResult.result;
      info = {
        contentPreview: (
          <PreviewIconContainer
            icon={
              <ContentIcon
                icon={
                  stackItem.url ? (
                    <UrlSrcIcon
                      name={i18n.t("icon_for_item")}
                      size="100%"
                      iconUrl={faviconSrcForSrcUrl(
                        stackItem.url,
                        32,
                        isDarkMode,
                      )}
                    />
                  ) : (
                    <UIIcon src={LinkLine} />
                  )
                }
                hasBackground={true}
                size="xlarge"
              />
            }
          />
        ),
        // Temporary. Unique stack treatment coming down the road.
        attributes: [{ label: i18n.t("last_updated_label"), value: "" }],
      };
      break;
    }
    case typeahead.ResultType.CalendarEvent: {
      info = { ...info, ...getCalendarEventInfo(scoredResult.result) };
      break;
    }
    case typeahead.ResultType.SearchResult: {
      const searchResult = scoredResult.result;
      info = {
        ...info,
        ...(isRecordTypeTagEvent(searchResult)
          ? getCalendarEventInfo(searchResult)
          : getContentInfo(searchResult)),
      };
      break;
    }
    case typeahead.ResultType.Recommendation:
      info = { ...info, ...getContentInfo(scoredResult.result) };
      break;
    case typeahead.ResultType.DesktopFile: {
      const { providerUpdateAtMs, description } = scoredResult.result;
      info = {
        ...info,
        subtitle: (
          <Text color="subtle">
            <Truncate location={0}>{description}</Truncate>
          </Text>
        ),
        contentPreview: (
          <Box
            borderWidth="1"
            borderStyle="Solid"
            borderColor="Border Subtle"
            borderRadius="Small"
            backgroundColor="Background Base"
          >
            <DesktopFileIcon
              result={scoredResult.result}
              size={BASE_PREVIEW_ICON_SIZE}
            />
          </Box>
        ),
        attributes: [
          {
            label: i18n.t("last_updated_label"),
            value: getTimeAgoStringFromTimestamp(providerUpdateAtMs),
          },
          {
            label: i18n.t("file_type_label"),
            value: getExtension(scoredResult.result)?.toUpperCase(),
          },
        ],
      };
      break;
    }
    case typeahead.ResultType.DesktopApplication: {
      info = {
        ...info,
        contentPreview: (
          <DesktopApplicationIcon
            result={scoredResult.result}
            size={BASE_PREVIEW_ICON_SIZE}
          />
        ),
      };
      break;
    }
    case typeahead.ResultType.SearchFilter:
    case typeahead.ResultType.PreviousQuery:
    case typeahead.ResultType.SuggestedQuery: {
      info = {
        contentPreview: <SearchShowcase />,
        previewSubtitle: i18n.t("search_preview_tagline"),
        align: "center",
        title: undefined,
        subtitle: undefined,
      };
      break;
    }
    case typeahead.ResultType.URLShortcut: {
      info = {
        title: undefined,
        subtitle: undefined,
        contentPreview: (
          <URLShortcutIcon
            result={scoredResult.result}
            size={BASE_PREVIEW_ICON_SIZE}
          />
        ),
      };
      break;
    }
    case typeahead.ResultType.MathCalculation: {
      const { query, answer } = scoredResult.result;
      info = {
        align: "top",
        contentPreview: (
          <Box style={{ width: "80%" }}>
            <MathShowcase equation={query} solution={answer} />
          </Box>
        ),
        title: undefined,
        subtitle: undefined,
      };
      break;
    }

    case typeahead.ResultType.AskSuggestion:
      // TODO: Finalize copy for subtitle
      info = getChatPreview(
        scoredResult.result.text,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.AskUserQuery:
      // TODO: Finalize copy for subtitle
      info = getChatPreview(
        scoredResult.result.query,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.RecentChat:
      // TODO: Finalize copy for subtitle
      info = getChatPreview(
        scoredResult.result.title,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.OrganizeUserQuery:
      // TODO: Add preview related to stacks
      break;
    case typeahead.ResultType.DraftWithSource:
      // TODO: Finalize copy for subtitle
      info = getChatPreview(
        scoredResult.result.title,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.DraftFromTemplate:
      // TODO: Finalize copy for subtitle
      info = getChatPreview(
        scoredResult.result.title,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.DraftFromTone:
      // TODO: Finalize copy for subtitle
      info = getChatPreview(
        scoredResult.result.title,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.WriteUserQuery:
      // TODO: Add additional context and UI for draft
      info = getChatPreview(
        scoredResult.result.query,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.MyProfileSuggestion:
    case typeahead.ResultType.PeopleFilter:
    case typeahead.ResultType.PeopleRecommendation: {
      const person = scoredResult.result;

      const attributes = [
        { label: i18n.t("email_label"), value: person.email },
      ];

      if (person.managerUuid) {
        const { managerUuid, email } = person;
        const domain = email?.includes("@")
          ? email.substring(email.indexOf("@"))
          : undefined;
        attributes.push({
          label: i18n.t("manager_label"),
          value: `${managerUuid}${domain}`,
        });
      }

      info = {
        title: person.displayName,
        subtitle: person.role,
        contentPreview: (
          <Avatar
            src={person.profilePhotoUrl}
            alt={person.displayName}
            size="large"
            hasNoOutline
            style={{ height: "72px", width: "72px" }}
          >
            {getInitials(person.displayName || person.email || "")}
          </Avatar>
        ),
        attributes,
      };
      break;
    }

    default:
      // TODO: Add a default side panel display for non-specified result types
      scoredResult satisfies never;
      logger.warn(`Invalid result type in getTypeaheadResultInfo: ${type}`);
      break;
  }

  return {
    title: result.title,
    subtitle: result.subtitle,
    ...info,
  };
};
